// Text catalog for the Om os / About us page.
// Images, the branch-icon list and the team data array live in
// src/pages/OmOsPage.tsx (names/images kept; only prose is translated here).

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type Stat = { value: string; label: string };
type ValueCard = { title: string; p1: string; p2: string };

type OmOsStrings = {
  meta: PageMeta;
  hero: { title: string; p1: string; p2: string; p3: string };
  about: { heading: string; p1: string; p2: string; branchLabels: string[] };
  history: { heading: string; p1: string; p2: string; photoAlts: [string, string, string] };
  partner: { heading: string; p1: string; p2: string; stats: Stat[] };
  phases: { body: string; imgAlt: string };
  fullService: { heading: string; p1: string; p2: string; imgAlt: string };
  it: { heading: string; p1: string; cta: string; imgAlt: string };
  languageQuote: { text: string; name: string; role: string };
  worldwide: { heading: string; p1: string; p2: string; p3: string; mapAlt: string; cta: string };
  values: { heading: string; intro: string };
  tqe: { heading: string; p1: string; p2: string; imgAlt: string; cards: ValueCard[] };
  sustainability: { heading: string; p1: string; p2: string; p3: string; p4: string; imgAlt: string };
  diversity: { heading: string; p1: string; p2: string; p3: string; p4: string; ctaBefore: string; ctaAfter: string; imgAlt: string };
  leadership: { heading: string };
  teamRoles: string[];
};

export const omosStrings: Record<Locale, OmOsStrings> = {
  da: {
    meta: {
      title: "Om os - Erfarne konsulenter til byggeriet - MI Support",
      description:
        "MI Support har over 25 års erfaring i komplekse byggeprojekter, hvor tid, kvalitet og økonomi er i fokus.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3787_1350x900.jpg",
    },
    hero: {
      title: "Om os",
      p1: "MI Support har over 25 års erfaring i komplekse byggeprojekter, hvor tid, kvalitet og økonomi er i fokus.",
      p2: "Vi leverer skræddersyede løsninger, der forener bæredygtighed, innovation og effektiv projektledelse. Med et erfarent team og en stærk værdisætning er vi en pålidelig partner både nationalt og internationalt.",
      p3: "Lær os bedre at kende her.",
    },
    about: {
      heading: "Om MI Support",
      p1: "MI Support A/S er en moderne og uafhængig konsulent-, ingeniør- og rådgivningsvirksomhed med mere end 25 års erfaring i byggebranchen. Vi er specialiserede i at levere skræddersyede løsninger og viden til større, prestigefyldte og ofte yderst komplekse byggeprojekter.",
      p2: "MI Support har solid erfaring med at styre byggeprocesser ved både nybyggeri og renoveringsopgaver, herunder udvikling af procesanlæg, laboratorier, hospitaler og kontorfaciliteter.{{break}}Gennem årene har vi opbygget dybdegående erfaring på tværs af en række brancher. Klik på en branche for at læse mere om vores kompetencer inden for området.",
      branchLabels: [
        "Pharma & life science",
        "Industri & produktion",
        "Datacentre & IT",
        "Hospitaler & sundhed",
        "Kontor & domiciler",
        "Energi & infrastruktur",
        "Transport & logistik",
      ],
    },
    history: {
      heading: "MI Supports historie",
      p1: "Vores rødder går tilbage til december 2000, hvor virksomheden blev stiftet af Sonny Christensen under navnet STC Svejse- og Montageservice. Fokus var primært på ydelser inden for smedefaget, og de første projekter bestod især i bygge- og montageopgaver på Carlsbergs bryggerier.",
      p2: "I løbet af det første halve år udviklede STC Svejse- og Montageservice sig til en mere rådgivende og konsulterende virksomhed, og i april 2001 blev vi officielt til Montage- og Ingeniørsupport, heraf navnet MI Support, som virksomheden hedder den dag i dag.",
      photoAlts: [
        "Historisk foto: tre montører ved en stålkonstruktion",
        "Historisk foto: montagearbejde på et procesanlæg",
        "Historisk foto: metalarbejde i værkstedet",
      ],
    },
    partner: {
      heading: "MI Support som samarbejdspartner",
      p1: "I MI Support er vi kendte for vores evne til at forstå og tilpasse os vores kunders og samarbejdspartneres specifikke behov. Vores tilgang bygger på en grundig analyse af opgaven, hvilket sikrer, at vi leverer præcis den ydelse, der matcher projektets krav.",
      p2: "Gennem professionel rådgivning og et tæt samarbejde med vores kunder skaber vi løsninger, der fører til succesfulde resultater og langvarige relationer.",
      stats: [
        { value: "+25", label: "Antal år vi har eksisteret" },
        { value: "+500", label: "Antal projekter vi har været en del af" },
        { value: "+15", label: "Antal lande vi har udført projekter i" },
      ],
    },
    phases: {
      body: "Med dygtige og erfarne konsulenter, der har dyb indsigt i alle stadier i PAM (projektets aktivitets- og milepælsplan), kan vi bidrage værdifuldt gennem alle faser af byggeprocessen, fra den indledende planlægning til den endelige overdragelse.",
      imgAlt: "PAM, projekt og milepælsplan",
    },
    fullService: {
      heading: "Full-service konsulentydelser",
      p1: "Vi tilbyder et bredt udvalg af full-service konsulentydelser, herunder bygge- og projektledelse, sikkerhedsledelse (HSE), tidsplanlægning og controlling samt ekspertise på flere specialistområder.",
      p2: "Vores konsulenter er eksperter i at tilpasse sig vores kunders værdisæt og procedurer, så de bliver hurtigt en naturlig integreret del af projektorganisationen.",
      imgAlt: "Møde på hovedkontoret",
    },
    it: {
      heading: "Skræddersyede IT-løsninger",
      p1: "Vores IT-afdeling MI Support IT leverer tilpassede IT-løsninger i form af både hardware, software og forskellige IT-konsulentydelser til byggeprojektet.",
      cta: "Gå til MI Support IT",
      imgAlt: "MI Support IT logo på væggen",
    },
    languageQuote: {
      text: "Fra MI Supports mange projekter verden over er vores konsulenter vant til at begå sig i engelsktalende projektorganisationer. Flere af vores medarbejdere har derudover stærke færdigheder i andre sprog som skandinavisk, tysk, fransk og spansk.",
      name: "Sonny Christensen",
      role: "Ejer & stifter af MI Support",
    },
    worldwide: {
      heading: "MI Support worldwide",
      p1: "I årenes løb har MI Support haft fornøjelsen af at samarbejde med en lang række anerkendte virksomheder i både Danmark og udlandet, og vi er stolte over, at mange af dem fortsat vælger os som en af deres foretrukne samarbejdspartnere den dag i dag.",
      p2: "Vores konsulenter har over de sidste 25 år arbejdet på mange komplekse byggeprojekter i Europa, Asien og USA og kan derfor bidrage med værdifuld erfaring, dybdegående indsigt og tværkulturel forståelse, som sikrer høj kvalitet og effektivitet i projekternes gennemførelse.",
      p3: "På kortet nedenfor kan du se, hvor i verden MI Support har været repræsenteret gennem tiden.",
      mapAlt: "Verdenskort over MI Support-lokationer",
      cta: "Se et udpluk af vores samarbejdspartnere",
    },
    values: {
      heading: "MI Supports værdier",
      intro: "I MI Support bygger vi vores konsulentydelser på en stærk kerne af værdier, der sikrer en effektiv og problemfri projektgennemførelse.",
    },
    tqe: {
      heading: "Tid, kvalitet og økonomi",
      p1: "Det er vores overbevisning, at en succesfuld byggeproces kræver en nøje balance mellem tid, kvalitet og økonomi, og vi stræber efter at gøre denne balance til en naturlig del af hvert eneste projekt, vi engagerer os i.",
      p2: "Vi tror på, at samarbejde er nøglen til et vellykket projekt. Ved at kombinere vores faglige ekspertise med en tæt dialog med vores kunder, skaber vi skræddersyede løsninger, hvor tid, kvalitet og økonomi går hånd i hånd.",
      imgAlt: "Konsulent skriver ved arbejdsbordet",
      cards: [
        {
          title: "Tid",
          p1: "Vi ved, at tid er en kritisk faktor i ethvert byggeprojekt. Derfor arbejder vi målrettet og struktureret for at holde projektet på rette spor.",
          p2: "Gennem detaljeret planlægning, løbende opfølgning og smidig kommunikation med alle involverede parter sikrer vi, at deadlines bliver overholdt, uden at gå på kompromis med kvaliteten.",
        },
        {
          title: "Kvalitet",
          p1: "Kvalitet er hjørnestenen i vores arbejde. MI Supports erfarne konsulenter leverer løsninger, der opfylder de højeste krav inden for branchen.",
          p2: "Med fokus på innovation og bæredygtighed skaber vi byggerier, der er holdbare og funktionelle, alt sammen tilpasset vores kunders behov og visioner.",
        },
        {
          title: "Økonomi",
          p1: "En god projektøkonomi handler om mere end blot at holde budgettet. For os i MI Support handler det også om at skabe værdi.",
          p2: "Vi sikrer, at hver investering i projektet bliver nøje vurderet, så ressourcerne bliver brugt optimalt. Gennemsigtighed og ansvarlighed er nøgleord i vores økonomistyring.",
        },
      ],
    },
    sustainability: {
      heading: "Bæredygtighed",
      p1: "I MI Support holder vi os opdaterede på nyeste tiltag indenfor bæredygtig udvikling og ledelse.",
      p2: "Siden starten af 2023 har vi med stolthed tilmed kunne kalde os ISO 14001-certificerede. Denne certificering er en internationalt anerkendt standard for miljøledelsessystemer, der bekræfter MI Supports engagement i bæredygtighed og ansvarlighed.",
      p3: "Det betyder, at vi ikke blot lever op til de højeste miljøstandarder, men også konstant arbejder på at forbedre vores processer og minimere vores miljøpåvirkning internt i virksomheden.",
      p4: "Ved at vælge en ISO 14001-certificeret samarbejdspartner som MI Support, sikrer I jer en rådgiver, der har bæredygtighed som en integreret del af virksomhedskulturen.",
      imgAlt: "Mødegruppe i MI Support",
    },
    diversity: {
      heading: "Diversitet",
      p1: "I MI Support er diversitet blandt vores medarbejdere en stor del af vores virksomhedskultur.",
      p2: "Vi værdsætter forskelligheder i køn, alder, etnicitet, religion, seksuel orientering og andre unikke karakteristika, da vi mener, at disse perspektiver beriger vores arbejdsplads og styrker vores evne til at levere innovative løsninger til vores kunder.",
      p3: "Ved at fremme en inkluderende kultur sikrer vi, at alle medarbejdere føler sig værdsatte og har lige muligheder for at bidrage og udvikle sig.",
      p4: "Vores engagement i diversitet og inklusion gør os ligeledes i stand til at tilpasse os de forskellige kulturer og arbejdsmiljøer hos vores kunder, og at imødekomme deres unikke behov på en mere effektiv og empatisk måde.",
      ctaBefore: "Læs mere om",
      ctaAfter: "som arbejdsplads",
      imgAlt: "Møde med Sonny Christensen",
    },
    leadership: {
      heading: "Ledelsen i MI Support",
    },
    teamRoles: [
      "Stifter & ejer",
      "IT & økonomi",
      "Byggeledelse & HR",
      "Byggeledelse & kvalitet",
    ],
  },

  en: {
    meta: {
      title: "About us - Experienced construction consultants - MI Support",
      description:
        "MI Support has over 25 years of experience in complex construction projects where time, quality and economy are in focus.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3787_1350x900.jpg",
    },
    hero: {
      title: "About us",
      p1: "MI Support has over 25 years of experience in complex construction projects where time, quality and economy are in focus.",
      p2: "We deliver tailored solutions that combine sustainability, innovation and efficient project management. With an experienced team and a strong set of values, we are a reliable partner both nationally and internationally.",
      p3: "Get to know us better here.",
    },
    about: {
      heading: "About MI Support",
      p1: "MI Support A/S is a modern and independent consulting, engineering and advisory company with more than 25 years of experience in the construction industry. We specialise in delivering tailored solutions and knowledge to larger, prestigious and often highly complex construction projects.",
      p2: "MI Support has solid experience in managing construction processes for both new builds and renovation tasks, including the development of process plants, laboratories, hospitals and office facilities.{{break}}Over the years we have built up in-depth experience across a range of industries. Click on an industry to read more about our competencies within the area.",
      branchLabels: [
        "Pharma & life science",
        "Industry & production",
        "Data centers & IT",
        "Hospitals & healthcare",
        "Office & headquarters",
        "Energy & infrastructure",
        "Transport & logistics",
      ],
    },
    history: {
      heading: "MI Support's history",
      p1: "Our roots go back to December 2000, when the company was founded by Sonny Christensen under the name STC Svejse- og Montageservice. The focus was primarily on services within the metalworking trade, and the first projects consisted mainly of construction and assembly tasks at Carlsberg's breweries.",
      p2: "During the first six months, STC Svejse- og Montageservice developed into a more advisory and consulting company, and in April 2001 we officially became Montage- og Ingeniørsupport, hence the name MI Support, which the company is called to this day.",
      photoAlts: [
        "Historical photo: three fitters at a steel structure",
        "Historical photo: assembly work on a process plant",
        "Historical photo: metalwork in the workshop",
      ],
    },
    partner: {
      heading: "MI Support as a partner",
      p1: "At MI Support we are known for our ability to understand and adapt to the specific needs of our customers and partners. Our approach is based on a thorough analysis of the task, which ensures that we deliver exactly the service that matches the project's requirements.",
      p2: "Through professional advice and close collaboration with our customers, we create solutions that lead to successful results and long-lasting relationships.",
      stats: [
        { value: "+25", label: "Number of years we have existed" },
        { value: "+500", label: "Number of projects we have been part of" },
        { value: "+15", label: "Number of countries we have carried out projects in" },
      ],
    },
    phases: {
      body: "With skilled and experienced consultants who have deep insight into all stages of the PAM (the project's activity and milestone plan), we can contribute valuably through all phases of the construction process, from the initial planning to the final handover.",
      imgAlt: "PAM, project and milestone plan",
    },
    fullService: {
      heading: "Full-service consulting services",
      p1: "We offer a wide range of full-service consulting services, including construction and project management, safety management (HSE), scheduling and controlling as well as expertise in several specialist areas.",
      p2: "Our consultants are experts at adapting to our customers' values and procedures, so they quickly become a naturally integrated part of the project organisation.",
      imgAlt: "Meeting at headquarters",
    },
    it: {
      heading: "Tailored IT solutions",
      p1: "Our IT department MI Support IT delivers customised IT solutions in the form of both hardware, software and various IT consulting services for the construction project.",
      cta: "Go to MI Support IT",
      imgAlt: "MI Support IT logo on the wall",
    },
    languageQuote: {
      text: "From MI Support's many projects around the world, our consultants are used to operating in English-speaking project organisations. Several of our employees also have strong skills in other languages such as Scandinavian, German, French and Spanish.",
      name: "Sonny Christensen",
      role: "Owner & founder of MI Support",
    },
    worldwide: {
      heading: "MI Support worldwide",
      p1: "Over the years, MI Support has had the pleasure of collaborating with a wide range of renowned companies in both Denmark and abroad, and we are proud that many of them continue to choose us as one of their preferred partners to this day.",
      p2: "Over the past 25 years, our consultants have worked on many complex construction projects in Europe, Asia and the USA and can therefore contribute valuable experience, in-depth insight and cross-cultural understanding, which ensures high quality and efficiency in the completion of the projects.",
      p3: "On the map below you can see where in the world MI Support has been represented over time.",
      mapAlt: "World map of MI Support locations",
      cta: "See a selection of our business partners",
    },
    values: {
      heading: "MI Support's values",
      intro: "At MI Support we build our consulting services on a strong core of values that ensure an efficient and seamless project completion.",
    },
    tqe: {
      heading: "Time, quality and economy",
      p1: "It is our conviction that a successful construction process requires a careful balance between time, quality and economy, and we strive to make this balance a natural part of every single project we engage in.",
      p2: "We believe that collaboration is the key to a successful project. By combining our professional expertise with close dialogue with our customers, we create tailored solutions where time, quality and economy go hand in hand.",
      imgAlt: "Consultant writing at the desk",
      cards: [
        {
          title: "Time",
          p1: "We know that time is a critical factor in any construction project. That is why we work purposefully and in a structured way to keep the project on the right track.",
          p2: "Through detailed planning, ongoing follow-up and smooth communication with all involved parties, we ensure that deadlines are met without compromising on quality.",
        },
        {
          title: "Quality",
          p1: "Quality is the cornerstone of our work. MI Support's experienced consultants deliver solutions that meet the highest requirements in the industry.",
          p2: "With a focus on innovation and sustainability, we create constructions that are durable and functional, all adapted to our customers' needs and visions.",
        },
        {
          title: "Economy",
          p1: "A good project economy is about more than just keeping the budget. For us at MI Support, it is also about creating value.",
          p2: "We ensure that every investment in the project is carefully assessed, so the resources are used optimally. Transparency and accountability are key words in our financial management.",
        },
      ],
    },
    sustainability: {
      heading: "Sustainability",
      p1: "At MI Support we keep up to date with the latest initiatives within sustainable development and management.",
      p2: "Since the beginning of 2023, we have proudly also been able to call ourselves ISO 14001 certified. This certification is an internationally recognised standard for environmental management systems that confirms MI Support's commitment to sustainability and responsibility.",
      p3: "This means that we not only live up to the highest environmental standards, but also constantly work to improve our processes and minimise our environmental impact internally in the company.",
      p4: "By choosing an ISO 14001 certified partner like MI Support, you ensure yourself an advisor who has sustainability as an integrated part of the corporate culture.",
      imgAlt: "Meeting group at MI Support",
    },
    diversity: {
      heading: "Diversity",
      p1: "At MI Support, diversity among our employees is a large part of our corporate culture.",
      p2: "We value differences in gender, age, ethnicity, religion, sexual orientation and other unique characteristics, as we believe that these perspectives enrich our workplace and strengthen our ability to deliver innovative solutions to our customers.",
      p3: "By promoting an inclusive culture, we ensure that all employees feel valued and have equal opportunities to contribute and develop.",
      p4: "Our commitment to diversity and inclusion also enables us to adapt to the different cultures and working environments of our customers, and to meet their unique needs in a more efficient and empathetic way.",
      ctaBefore: "Read more about",
      ctaAfter: "as a workplace",
      imgAlt: "Meeting with Sonny Christensen",
    },
    leadership: {
      heading: "The management at MI Support",
    },
    teamRoles: [
      "Founder & owner",
      "IT & finance",
      "Construction management & HR",
      "Construction management & quality",
    ],
  },

  de: {
    meta: {
      title: "Über uns - Erfahrene Berater für den Bau - MI Support",
      description:
        "MI Support verfügt über mehr als 25 Jahre Erfahrung mit komplexen Bauprojekten, bei denen Zeit, Qualität und Wirtschaftlichkeit im Fokus stehen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK3787_1350x900.jpg",
    },
    hero: {
      title: "Über uns",
      p1: "MI Support verfügt über mehr als 25 Jahre Erfahrung mit komplexen Bauprojekten, bei denen Zeit, Qualität und Wirtschaftlichkeit im Fokus stehen.",
      p2: "Wir liefern maßgeschneiderte Lösungen, die Nachhaltigkeit, Innovation und effiziente Projektleitung vereinen. Mit einem erfahrenen Team und einer starken Werteorientierung sind wir ein zuverlässiger Partner sowohl national als auch international.",
      p3: "Lernen Sie uns hier näher kennen.",
    },
    about: {
      heading: "Über MI Support",
      p1: "MI Support A/S ist ein modernes und unabhängiges Beratungs-, Ingenieur- und Consultingunternehmen mit mehr als 25 Jahren Erfahrung in der Baubranche. Wir sind darauf spezialisiert, maßgeschneiderte Lösungen und Wissen für größere, prestigeträchtige und oft äußerst komplexe Bauprojekte zu liefern.",
      p2: "MI Support verfügt über solide Erfahrung in der Steuerung von Bauprozessen sowohl bei Neubauten als auch bei Renovierungsaufgaben, einschließlich der Entwicklung von Prozessanlagen, Laboren, Krankenhäusern und Büroeinrichtungen.{{break}}Im Laufe der Jahre haben wir umfassende Erfahrung in einer Reihe von Branchen aufgebaut. Klicken Sie auf eine Branche, um mehr über unsere Kompetenzen in diesem Bereich zu erfahren.",
      branchLabels: [
        "Pharma & Life Science",
        "Industrie & Produktion",
        "Rechenzentren & IT",
        "Krankenhäuser & Gesundheit",
        "Büro & Hauptsitze",
        "Energie & Infrastruktur",
        "Transport & Logistik",
      ],
    },
    history: {
      heading: "Die Geschichte von MI Support",
      p1: "Unsere Wurzeln reichen zurück bis Dezember 2000, als das Unternehmen von Sonny Christensen unter dem Namen STC Svejse- og Montageservice gegründet wurde. Der Fokus lag primär auf Leistungen im Schlosserhandwerk, und die ersten Projekte bestanden vor allem aus Bau- und Montagearbeiten in den Brauereien von Carlsberg.",
      p2: "Im Laufe des ersten halben Jahres entwickelte sich STC Svejse- og Montageservice zu einem stärker beratenden und konsultierenden Unternehmen, und im April 2001 wurden wir offiziell zu Montage- og Ingeniørsupport, daher der Name MI Support, wie das Unternehmen bis heute heißt.",
      photoAlts: [
        "Historisches Foto: drei Monteure an einer Stahlkonstruktion",
        "Historisches Foto: Montagearbeiten an einer Prozessanlage",
        "Historisches Foto: Metallarbeiten in der Werkstatt",
      ],
    },
    partner: {
      heading: "MI Support als Partner",
      p1: "Bei MI Support sind wir bekannt für unsere Fähigkeit, die spezifischen Bedürfnisse unserer Kunden und Partner zu verstehen und uns daran anzupassen. Unser Ansatz basiert auf einer gründlichen Analyse der Aufgabe, was sicherstellt, dass wir genau die Leistung liefern, die den Anforderungen des Projekts entspricht.",
      p2: "Durch professionelle Beratung und eine enge Zusammenarbeit mit unseren Kunden schaffen wir Lösungen, die zu erfolgreichen Ergebnissen und langjährigen Beziehungen führen.",
      stats: [
        { value: "+25", label: "Anzahl der Jahre, die wir bestehen" },
        { value: "+500", label: "Anzahl der Projekte, an denen wir beteiligt waren" },
        { value: "+15", label: "Anzahl der Länder, in denen wir Projekte durchgeführt haben" },
      ],
    },
    phases: {
      body: "Mit kompetenten und erfahrenen Beratern, die tiefen Einblick in alle Phasen des PAM (Aktivitäts- und Meilensteinplan des Projekts) haben, können wir wertvoll durch alle Phasen des Bauprozesses beitragen – von der anfänglichen Planung bis zur endgültigen Übergabe.",
      imgAlt: "PAM, Projekt- und Meilensteinplan",
    },
    fullService: {
      heading: "Full-Service-Beratungsleistungen",
      p1: "Wir bieten ein breites Angebot an Full-Service-Beratungsleistungen, darunter Bau- und Projektleitung, Sicherheitsmanagement (HSE), Terminplanung und Controlling sowie Expertise in mehreren Spezialgebieten.",
      p2: "Unsere Berater sind Experten darin, sich an die Werte und Verfahren unserer Kunden anzupassen, sodass sie schnell ein natürlich integrierter Teil der Projektorganisation werden.",
      imgAlt: "Besprechung in der Zentrale",
    },
    it: {
      heading: "Maßgeschneiderte IT-Lösungen",
      p1: "Unsere IT-Abteilung MI Support IT liefert maßgeschneiderte IT-Lösungen in Form von Hardware, Software und verschiedenen IT-Beratungsleistungen für das Bauprojekt.",
      cta: "Zu MI Support IT",
      imgAlt: "MI Support IT Logo an der Wand",
    },
    languageQuote: {
      text: "Aus den vielen Projekten von MI Support weltweit sind unsere Berater es gewohnt, sich in englischsprachigen Projektorganisationen zu bewegen. Mehrere unserer Mitarbeiter verfügen darüber hinaus über starke Kenntnisse in anderen Sprachen wie Skandinavisch, Deutsch, Französisch und Spanisch.",
      name: "Sonny Christensen",
      role: "Inhaber & Gründer von MI Support",
    },
    worldwide: {
      heading: "MI Support weltweit",
      p1: "Im Laufe der Jahre hatte MI Support das Vergnügen, mit einer Vielzahl renommierter Unternehmen sowohl in Dänemark als auch im Ausland zusammenzuarbeiten, und wir sind stolz darauf, dass viele von ihnen uns bis heute weiterhin als einen ihrer bevorzugten Partner wählen.",
      p2: "In den letzten 25 Jahren haben unsere Berater an vielen komplexen Bauprojekten in Europa, Asien und den USA gearbeitet und können daher wertvolle Erfahrung, tiefgehenden Einblick und interkulturelles Verständnis einbringen, was hohe Qualität und Effizienz bei der Durchführung der Projekte sicherstellt.",
      p3: "Auf der Karte unten sehen Sie, wo auf der Welt MI Support im Laufe der Zeit vertreten war.",
      mapAlt: "Weltkarte der MI Support-Standorte",
      cta: "Sehen Sie eine Auswahl unserer Geschäftspartner",
    },
    values: {
      heading: "Die Werte von MI Support",
      intro: "Bei MI Support bauen wir unsere Beratungsleistungen auf einem starken Kern von Werten auf, die eine effiziente und reibungslose Projektdurchführung sicherstellen.",
    },
    tqe: {
      heading: "Zeit, Qualität und Wirtschaftlichkeit",
      p1: "Es ist unsere Überzeugung, dass ein erfolgreicher Bauprozess eine sorgfältige Balance zwischen Zeit, Qualität und Wirtschaftlichkeit erfordert, und wir streben danach, diese Balance zu einem natürlichen Teil jedes einzelnen Projekts zu machen, in das wir uns einbringen.",
      p2: "Wir glauben, dass Zusammenarbeit der Schlüssel zu einem erfolgreichen Projekt ist. Indem wir unsere fachliche Expertise mit einem engen Dialog mit unseren Kunden kombinieren, schaffen wir maßgeschneiderte Lösungen, bei denen Zeit, Qualität und Wirtschaftlichkeit Hand in Hand gehen.",
      imgAlt: "Berater schreibt am Arbeitstisch",
      cards: [
        {
          title: "Zeit",
          p1: "Wir wissen, dass Zeit ein kritischer Faktor in jedem Bauprojekt ist. Daher arbeiten wir zielgerichtet und strukturiert, um das Projekt auf dem richtigen Kurs zu halten.",
          p2: "Durch detaillierte Planung, laufende Nachverfolgung und reibungslose Kommunikation mit allen Beteiligten stellen wir sicher, dass Fristen eingehalten werden, ohne Kompromisse bei der Qualität einzugehen.",
        },
        {
          title: "Qualität",
          p1: "Qualität ist der Grundpfeiler unserer Arbeit. Die erfahrenen Berater von MI Support liefern Lösungen, die die höchsten Anforderungen der Branche erfüllen.",
          p2: "Mit Fokus auf Innovation und Nachhaltigkeit schaffen wir Bauwerke, die langlebig und funktional sind, alles angepasst an die Bedürfnisse und Visionen unserer Kunden.",
        },
        {
          title: "Wirtschaftlichkeit",
          p1: "Eine gute Projektwirtschaftlichkeit bedeutet mehr als nur das Budget einzuhalten. Für uns bei MI Support geht es auch darum, Wert zu schaffen.",
          p2: "Wir stellen sicher, dass jede Investition in das Projekt sorgfältig bewertet wird, sodass die Ressourcen optimal genutzt werden. Transparenz und Verantwortlichkeit sind Schlüsselwörter in unserem Finanzmanagement.",
        },
      ],
    },
    sustainability: {
      heading: "Nachhaltigkeit",
      p1: "Bei MI Support halten wir uns über die neuesten Initiativen im Bereich nachhaltige Entwicklung und Führung auf dem Laufenden.",
      p2: "Seit Anfang 2023 können wir uns mit Stolz auch ISO 14001-zertifiziert nennen. Diese Zertifizierung ist ein international anerkannter Standard für Umweltmanagementsysteme, der das Engagement von MI Support für Nachhaltigkeit und Verantwortung bestätigt.",
      p3: "Das bedeutet, dass wir nicht nur die höchsten Umweltstandards erfüllen, sondern auch ständig daran arbeiten, unsere Prozesse zu verbessern und unsere Umweltauswirkungen intern im Unternehmen zu minimieren.",
      p4: "Indem Sie einen ISO 14001-zertifizierten Partner wie MI Support wählen, sichern Sie sich einen Berater, der Nachhaltigkeit als integralen Bestandteil der Unternehmenskultur hat.",
      imgAlt: "Besprechungsgruppe bei MI Support",
    },
    diversity: {
      heading: "Diversität",
      p1: "Bei MI Support ist Diversität unter unseren Mitarbeitern ein großer Teil unserer Unternehmenskultur.",
      p2: "Wir schätzen Unterschiede in Geschlecht, Alter, Ethnizität, Religion, sexueller Orientierung und anderen einzigartigen Merkmalen, da wir glauben, dass diese Perspektiven unseren Arbeitsplatz bereichern und unsere Fähigkeit stärken, innovative Lösungen für unsere Kunden zu liefern.",
      p3: "Durch die Förderung einer inklusiven Kultur stellen wir sicher, dass sich alle Mitarbeiter wertgeschätzt fühlen und gleiche Möglichkeiten haben, beizutragen und sich zu entwickeln.",
      p4: "Unser Engagement für Diversität und Inklusion versetzt uns ebenfalls in die Lage, uns an die unterschiedlichen Kulturen und Arbeitsumgebungen unserer Kunden anzupassen und ihren einzigartigen Bedürfnissen auf effizientere und empathischere Weise gerecht zu werden.",
      ctaBefore: "Mehr erfahren über",
      ctaAfter: "als Arbeitsplatz",
      imgAlt: "Besprechung mit Sonny Christensen",
    },
    leadership: {
      heading: "Die Geschäftsführung von MI Support",
    },
    teamRoles: [
      "Gründer & Inhaber",
      "IT & Finanzen",
      "Bauleitung & HR",
      "Bauleitung & Qualität",
    ],
  },
};
